import React from 'react';

import { ImageServiceProvider } from '@24i/nxg-sdk-gluons/src/context/ImageService';
import { LayoutProvider } from '@24i/nxg-sdk-gluons/src/context/Layout';
import { MonitoringDataProvider } from '@24i/nxg-sdk-smartott-shared/src/context/MonitoringData';
import { AnalyticsProvider } from '@24i/nxg-sdk-smartott-shared/src/context/Analytics';

import { AppNavigationProvider } from '../AppNavigation';
import { CastProvider } from '../Cast';
import { AppStartProvider } from '../AppStartContext';
import { DefaultSDKProvidersProps } from './types';
import { PlayerEngineProvider } from '../PlayerEngine';
import { defaultEngineFactory } from '../PlayerEngine/resolution';
import { SubscriptionsHandlerProvider } from '../SubscriptionsHandlerProvider';
import { useAppSettingsData } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';

export const DefaultSDKProviders = (props: DefaultSDKProvidersProps) => {
    const { clients, children } = props;
    const {
        appNavigationClient,
        imageServiceClient,
        sentryMonitoringDataClient,
        // dataPlatformClient,
        googleAnalyticsClient,
        twentyFourIQClient,
        layoutClient,
        appStartClient,
    } = clients;
    const webPlayerConfiguration = useAppSettingsData().serviceConfigData.data?.custom?.webPlayer;

    const engineProvider = async () => {
        const { userAgent } = navigator;
        const isIOS = (/iPhone|iPad|iPod/).test(userAgent);
        const isAndroid = /Android/.test(userAgent);
        const isChrome = /Chrome/.test(userAgent);
        const isSafari = /Safari/.test(userAgent) && !isChrome;

        enum PlayerName {
            shaka = 'shaka',
            hlsjs = 'hlsjs',
            html5 = 'html5',
        };

        /*
         * - iOS is not supported by HLS.js at all, it does not have MSE, so we need to use
         *   its native HLS support, either through Shaka or HTML5 directly
         * - on Desktop we (generally) want to use HLS.js
         * - otherwise we need to experiment what works good enough
         * browser_os, browser, os, 'player'
         */
        let playerName = webPlayerConfiguration?.base || PlayerName.hlsjs;
        if (isIOS) {
            playerName = webPlayerConfiguration?.ios || PlayerName.shaka;
        } else if (isAndroid) {
            playerName = webPlayerConfiguration?.android || PlayerName.hlsjs;
        } else if (isSafari) {
            playerName = webPlayerConfiguration?.safari || PlayerName.shaka;
        } else if (isChrome) {
            playerName = webPlayerConfiguration?.chrome || PlayerName.hlsjs;
        }

        if (playerName === PlayerName.hlsjs) {
            const { HLSJSEngine } = await import('@24i/player-hlsjs');
            return new HLSJSEngine();
        } else if (playerName === PlayerName.shaka) {
            const { ShakaPlayerEngine } = await import('@24i/player-shaka-player');
            return new ShakaPlayerEngine();
        } else if (playerName === PlayerName.html5) {
            const { HTML5VideoEngine } = await import('@24i/player-html5');
            return new HTML5VideoEngine();
        } else {
            return defaultEngineFactory();
        }
    };

    return (
        <AppNavigationProvider client={appNavigationClient}>
            <ImageServiceProvider client={imageServiceClient}>
                <MonitoringDataProvider
                    clients={[sentryMonitoringDataClient /* , dataPlatformClient */]}
                >
                    <AnalyticsProvider clients={[googleAnalyticsClient, twentyFourIQClient]}>
                        <LayoutProvider client={layoutClient}>
                            <CastProvider>
                                <PlayerEngineProvider engine={engineProvider}>
                                    <AppStartProvider client={appStartClient}>
                                        <SubscriptionsHandlerProvider>
                                            {children}
                                        </SubscriptionsHandlerProvider>
                                    </AppStartProvider>
                                </PlayerEngineProvider>
                            </CastProvider>
                        </LayoutProvider>
                    </AnalyticsProvider>
                </MonitoringDataProvider>
            </ImageServiceProvider>
        </AppNavigationProvider>
    );
};
